import { useEffect } from "react";
import { Form } from "antd";
import SingleModel from "views/pages/EnrichmentPage/PreviewsSection/ModelList/SingleModel";
import { usePromptSupportOptions } from "hooks/useFeatureFlagHooks";
import styles from "./style.module.scss";
import enrichmentPageStyles from "views/pages/EnrichmentPage/style.module.scss";
import {
  EnrichmentModelForPreview,
  ModelVarient,
  SelectionAlertModel,
} from "types";
import Loader from "views/pages/DatasetCreationPage/DatasetCreationWizard/Loader";
import classNames from "classnames";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";
import SelectionAlertModal from "views/pages/EnrichmentPage/SelectionAlertModal";
import { createContent } from "views/pages/EnrichmentPage/helpers";

export interface SelectEnrichmentModelTypes {
  selectedModelIds: string[];
  isModelListLoading: boolean;
  fetchEnrichmentModels: () => void;
  modelList: EnrichmentModelForPreview[];
  handleModelSelection: (model: string) => void;
  handleDeclineConfirmation: () => void;
  handleAcceptOverrideConfirmation: () => void;
  selectionAlertModel: SelectionAlertModel | null;
  modelDependencyMessages: Record<string, string>;
  disabledModelIds: string[];
}

const SelectEnrichmentModel = ({
  modelList,
  selectedModelIds,
  isModelListLoading,
  handleModelSelection,
  fetchEnrichmentModels,
  handleDeclineConfirmation,
  handleAcceptOverrideConfirmation,
  selectionAlertModel,
  modelDependencyMessages,
  disabledModelIds,
}: SelectEnrichmentModelTypes) => {
  const promptSupportEnabled = usePromptSupportOptions().enabled;

  useEffect(() => {
    fetchEnrichmentModels();
  }, [fetchEnrichmentModels]);

  const renderSelectionAlertModal = () => {
    if (!selectionAlertModel) return <noscript />;

    const isAlert = selectionAlertModel.selectionType === "alert";
    const titleText = isAlert ? "Just a quick heads-up" : "Override Operation";

    const iconClassnames = classNames(enrichmentPageStyles.titleIcon, {
      [enrichmentPageStyles.warningIcon]: isAlert,
    });

    // Title Icon
    const icon = (
      <div className={iconClassnames}>
        <DangerCircleIcon variant="filled" />
      </div>
    );

    const title = (
      <span className={enrichmentPageStyles.title}>{titleText}</span>
    );
    const content = createContent(selectionAlertModel);

    return (
      <SelectionAlertModal
        icon={icon}
        title={title}
        content={content}
        onDecline={handleDeclineConfirmation}
        onAccept={handleAcceptOverrideConfirmation}
      />
    );
  };

  if (isModelListLoading) return <Loader />;
  return (
    <div className={styles.enrichmentModelSection}>
      <div>
        <div className={styles.header}>Select Enrichment Models </div>
        <div className={styles.subHeader}>
          Enhance Your Dataset with one or more models to upgrade your uploaded
          data and exploration. Our models can detect object, add labels and
          improve your search capabilities.
        </div>
      </div>
      <Form.Item
        name="selectedModelIds"
        initialValue={[]}
        style={{ marginBottom: 0 }}
      >
        <div className={styles.modelListWrapper}>
          {modelList.map((model: EnrichmentModelForPreview, index: number) => {
            const isSelected = selectedModelIds.includes(model.id);
            const missingPrompt =
              promptSupportEnabled &&
              model.vqa_prompt &&
              !model.metadata?.prompt?.length;
            const disabled = disabledModelIds.includes(model.id);
            const dependencyMessage = modelDependencyMessages[model.id];
            return (
              <div key={index} className={styles.singleModelWrapper}>
                <SingleModel
                  model={model}
                  isSelected={isSelected}
                  onRemoveModel={() => {}}
                  onSelectModel={handleModelSelection}
                  missingPrompt={missingPrompt}
                  modelVarient={ModelVarient.CREATION}
                  disabled={disabled}
                  dependencyMessage={dependencyMessage}
                />
              </div>
            );
          })}
        </div>
      </Form.Item>
      {renderSelectionAlertModal()}
    </div>
  );
};
export default SelectEnrichmentModel;
